/**
 * This component uses Portable Text to render a post body.
 *
 * You can learn more about Portable Text on:
 * https://www.sanity.io/docs/block-content
 * https://github.com/portabletext/react-portabletext
 * https://portabletext.org/
 *
 */

import type { ModuleRichText, ModuleTextMedia } from "@lib/types";
import {
  PortableText,
  type PortableTextBlock,
  type PortableTextComponents,
} from "@portabletext/react";
import { stegaClean } from "@sanity/client/stega";
import Link from "next/link";
import type { ElementType } from "react";

import { cn } from "@/lib/ui/cn";
import { applyColorTheme, ColorTheme } from "@/lib/ui/colorTheme";
import { isExternalUrl } from "@/lib/url";

type SanityBlockType =
  | NonNullable<ModuleRichText["body"]>[number]
  | NonNullable<ModuleTextMedia["body"]>[number];

type TableRow = { _key?: string; _type: "tableRow"; cells?: string[] };
type TableValue = { _type: "table"; rows?: TableRow[] };

function renderCellWithBold(text: string) {
  if (!text) return null;
  const parts = text.split(/\*\*(.+?)\*\*/g);
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <strong key={i} className="text-foreground font-semibold">
        {part}
      </strong>
    ) : (
      part
    ),
  );
}

export type PortableTextValue = PortableTextBlock[] | SanityBlockType[];

export const portableTextLinkClassName =
  "typography-link-inherit text-foreground underline transition-colors hover:text-neutral-600";

export default function CustomPortableText({
  className,
  value,
  as = "div",
  theme,
  textClassName = "typography-text-regular-normal",
}: {
  className?: string;
  value?: PortableTextValue | null;
  as?: ElementType;
  theme?: ColorTheme;
  textClassName?: string;
}) {
  if (theme) {
    textClassName = applyColorTheme(theme, textClassName);
  }

  const components: PortableTextComponents = {
    block: {
      h1: ({ children }) => (
        <h1 className="typography-heading-1 text-foreground mb-6">{children}</h1>
      ),
      h2: ({ children }) => (
        <h2 className="typography-heading-2 text-foreground mb-5">{children}</h2>
      ),
      h3: ({ children }) => (
        <h3 className="typography-heading-3 text-foreground mb-4">{children}</h3>
      ),
      h4: ({ children }) => (
        <h4 className="typography-heading-4 text-foreground mb-3">{children}</h4>
      ),
      h5: ({ children }) => (
        <h5 className="typography-heading-5 text-foreground mb-2">{children}</h5>
      ),
      h6: ({ children }) => (
        <h6 className="typography-heading-6 text-foreground mb-1">{children}</h6>
      ),
      blockquote: ({ children }) => (
        <blockquote
          className={cn(
            "text-neutral-light my-6 border-l-4 border-neutral-300 pl-4 italic",
            textClassName,
          )}
        >
          {children}
        </blockquote>
      ),
      normal: ({ children, value }) => {
        // Renders a horizontal rule when the line text is "---"
        if (stegaClean(value?.children?.[0]?.text ?? "")?.trim() === "---") {
          return <hr className={cn("my-8 border-neutral-300", textClassName)} />;
        }
        return <p className={cn("text-foreground mb-4", textClassName)}>{children}</p>;
      },
      // Per-paragraph alignment styles (see block.ts). Each renders a paragraph
      // with the matching Tailwind alignment utility. Left is the default
      // (the "Normal" style), so it needs no dedicated handler.
      center: ({ children }) => (
        <p className={cn("text-foreground mb-4 text-center", textClassName)}>{children}</p>
      ),
      right: ({ children }) => (
        <p className={cn("text-foreground mb-4 text-right", textClassName)}>{children}</p>
      ),
      justify: ({ children }) => (
        <p className={cn("text-foreground mb-4 text-justify", textClassName)}>{children}</p>
      ),
    },
    marks: {
      link: ({ children, value }) => {
        const href = value?.href;
        const openBlank = value?.blank || isExternalUrl(href);
        return (
          <Link
            href={href}
            className={portableTextLinkClassName}
            rel={openBlank ? "noopener noreferrer" : undefined}
            target={openBlank ? "_blank" : undefined}
          >
            {children}
          </Link>
        );
      },
      strong: ({ children }) => (
        <strong className="text-foreground font-semibold">{children}</strong>
      ),
      em: ({ children }) => <em className="text-foreground italic">{children}</em>,
    },
    list: {
      bullet: ({ children }) => (
        <ul className={cn("text-foreground my-6 ml-6 list-disc space-y-2", textClassName)}>
          {children}
        </ul>
      ),
      number: ({ children }) => (
        <ol className={cn("text-foreground my-6 ml-6 list-decimal space-y-2", textClassName)}>
          {children}
        </ol>
      ),
    },
    listItem: {
      bullet: ({ children }) => <li className="pl-2">{children}</li>,
      number: ({ children }) => <li className="pl-2">{children}</li>,
    },
    types: {
      table: ({ value }: { value: TableValue }) => {
        const rows = value?.rows?.filter((row) => row?.cells?.length);
        if (!rows?.length) return null;
        return (
          <div className="my-6 overflow-x-auto">
            <table className="w-full table-fixed border-collapse">
              <tbody>
                {rows.map((row, rowIdx) => (
                  <tr key={row._key ?? rowIdx}>
                    {row.cells?.map((cell, cellIdx) => (
                      <td
                        key={cellIdx}
                        className={cn(
                          "text-foreground border border-neutral-300 p-4 align-top",
                          textClassName,
                        )}
                      >
                        {renderCellWithBold(cell)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      },
    },
  };

  if (!value?.length) return null;

  const filteredBlocks = value.filter((block) => {
    if (block._type !== "block") return true;
    return "children" in block && !!block.children && block.children.length > 0;
  }) as PortableTextBlock[];

  if (!filteredBlocks.length) return null;

  const Component = as;

  return (
    <Component className={cn("prose prose-lg text-foreground max-w-none", className)}>
      <PortableText components={components} value={filteredBlocks} />
    </Component>
  );
}
